import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, Loader2, Palette, Play, Volume2 } from 'lucide-react';
import { ThemePickerDialog } from '@/components/learningTutorial/ThemePickerDialog';
import { ThemeSlidePreview } from '@/components/learningTutorial/ThemeSlidePreview';
import { buildEditorLogo, resolveLogoPreviewUrl } from '@/lib/learningTutorial/logoBrand';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TwinneeBrand } from '@/components/TwinneeBrand';
import api from '@/lib/api';
import {
  LEARNING_TUTORIAL_SYNC_TIMEOUT_MS,
  LEARNING_TUTORIAL_TIMELINE_TIMEOUT_MS,
} from '@/lib/learningTutorial/apiTimeouts';
import { EMPTY_OPTIONS, fetchLearningTutorialOptions } from '@/lib/learningTutorial/catalog';
import { isProgrammingTopic } from '@/lib/learningTutorial/programmingContext';
import { MurfVoiceFields } from '@/components/tts/MurfVoiceFields';
import { languageToMurfLocale } from '@/lib/murfVoices';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export const LearningTutorialFlowPage = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [objective, setObjective] = useState('');
  const [audience, setAudience] = useState('beginners');
  const [duration, setDuration] = useState(5);
  const [prerequisites, setPrerequisites] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [includeCode, setIncludeCode] = useState(false);
  const [visualNotes, setVisualNotes] = useState(
    'Plain white diagram backgrounds; flowcharts with short labels only (no dense text). Code on right, list on left.',
  );

  const [language, setLanguage] = useState('en');
  const [themeId, setThemeId] = useState('slate_emerald');
  const [layoutMode, setLayoutMode] = useState('per_scene');
  const [ttsProvider, setTtsProvider] = useState('edge');
  const [ttsVoiceStyle, setTtsVoiceStyle] = useState('storyteller');
  const [ttsVoiceId, setTtsVoiceId] = useState('');
  const [ttsLocale, setTtsLocale] = useState('en-US');
  const [visualProvider, setVisualProvider] = useState('gemini');
  const [generateVisuals, setGenerateVisuals] = useState(true);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [logoMode, setLogoMode] = useState('none');
  const [logoText, setLogoText] = useState('');
  const [logoImageUrl, setLogoImageUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);

  const [projectId, setProjectId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [framesPreview, setFramesPreview] = useState([]);

  useEffect(() => {
    if (isProgrammingTopic(topic, objective, title)) {
      setIncludeCode(true);
    }
  }, [topic, objective, title]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catalog = await fetchLearningTutorialOptions(api);
        if (cancelled) return;
        setOptions(catalog);
        const d = catalog.defaults || {};
        if (d.theme_id) setThemeId(d.theme_id);
        if (d.layout_mode) setLayoutMode(d.layout_mode);
        if (d.language) setLanguage(d.language);
        if (d.tts_provider) setTtsProvider(d.tts_provider);
        if (d.tts_voice_style) setTtsVoiceStyle(d.tts_voice_style);
        if (d.visual_provider) setVisualProvider(d.visual_provider);
        if (typeof d.generate_visuals === 'boolean') setGenerateVisuals(d.generate_visuals);
      } catch {
        if (!cancelled) toast.error('Could not load tutorial options');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedTheme = useMemo(
    () => (options.themes || []).find((t) => t.id === themeId),
    [options.themes, themeId],
  );

  const ttsProviderMeta = useMemo(
    () => (options.tts_providers || []).find((p) => p.id === ttsProvider),
    [options.tts_providers, ttsProvider],
  );

  const elevenlabsVoices = options.elevenlabs_voices || [];
  const murfVoices = options.murf_voices || [];
  const murfLocales = options.murf_locales || [];

  const handleLogoModeChange = (m) => {
    setLogoMode(m);
    if (m === 'none') {
      setLogoText('');
      setLogoImageUrl('');
      setLogoFile(null);
    } else if (m === 'text') {
      setLogoImageUrl('');
      setLogoFile(null);
    } else if (m === 'image') {
      setLogoText('');
    }
  };

  const handleLogoFileSelect = (file) => {
    setLogoFile(file);
    if (logoImageUrl?.startsWith('blob:')) URL.revokeObjectURL(logoImageUrl);
    setLogoImageUrl(URL.createObjectURL(file));
  };

  const logoPreviewDraft = useMemo(
    () => ({
      mode: logoMode,
      text: logoText,
      previewUrl: logoImageUrl,
      url: logoImageUrl,
    }),
    [logoMode, logoText, logoImageUrl],
  );

  const intakePayload = () => ({
    topic: topic.trim(),
    learning_objective: objective.trim() || topic.trim(),
    audience,
    duration_minutes: Number(duration) || 5,
    language,
    prerequisites: prerequisites.trim(),
    key_points: keyPoints.split('\n').map((s) => s.trim()).filter(Boolean),
    include_code: includeCode,
    visual_style_notes: visualNotes.trim(),
    reference_image_urls: [],
    theme_id: themeId,
    layout_mode: layoutMode,
    tts_provider: ttsProvider,
    tts_voice_id: ttsVoiceId || null,
    tts_voice_style: ttsVoiceStyle,
    tts_locale: ttsProvider === 'murf' ? (ttsLocale || languageToMurfLocale(language)) : null,
    visual_provider: generateVisuals ? visualProvider : 'none',
    generate_visuals: generateVisuals,
  });

  const createAndRun = async () => {
    if (!title.trim() || topic.trim().length < 3) {
      toast.error('Title and topic (3+ chars) are required');
      return;
    }
    if (ttsProvider === 'murf' && !ttsVoiceId) {
      toast.error('Select a Murf voice');
      return;
    }
    if (ttsProviderMeta && !ttsProviderMeta.configured && ttsProvider !== 'edge') {
      toast.error(`${ttsProviderMeta.label} is not configured on the server`);
      return;
    }
    if (logoMode === 'text' && !logoText.trim()) {
      toast.error('Enter logo text or choose a different logo option');
      return;
    }
    if (logoMode === 'image' && !logoFile && !logoImageUrl) {
      toast.error('Upload a logo image');
      return;
    }
    if (logoMode === 'both' && (!logoText.trim() || (!logoFile && !logoImageUrl))) {
      toast.error('Logo needs both text and an image');
      return;
    }
    setStatus('working');
    try {
      const { data: created } = await api.post('/learning-tutorials/projects', {
        title: title.trim(),
        intake: intakePayload(),
      });
      const pid = created.project.id;
      setProjectId(pid);

      if (logoMode !== 'none') {
        let logoUrl = logoImageUrl;
        if (logoFile) {
          const fd = new FormData();
          fd.append('file', logoFile);
          fd.append('kind', 'logo');
          const { data: up } = await api.post('/learning-tutorials/upload', fd);
          logoUrl = up.url;
        } else if (logoUrl) {
          logoUrl = resolveLogoPreviewUrl(logoUrl, BACKEND_URL);
        }
        const logo = buildEditorLogo({ mode: logoMode, text: logoText, url: logoUrl });
        if (Object.keys(logo).length) {
          await api.patch(`/learning-tutorials/projects/${pid}/editor`, { logo });
        }
      }

      await api.post(`/learning-tutorials/projects/${pid}/generate-script`);
      setStatus('syncing');
      toast.message('Generating trainer narration audio (TTS)…');
      await api.post(`/learning-tutorials/projects/${pid}/narration-audio`, null, {
        timeout: LEARNING_TUTORIAL_SYNC_TIMEOUT_MS,
      });
      toast.message('Running WhisperX word alignment…');
      await api.post(`/learning-tutorials/projects/${pid}/narration-align`, null, {
        timeout: LEARNING_TUTORIAL_SYNC_TIMEOUT_MS,
      });
      toast.message('Finalizing timeline with measured audio clocks…');
      const { data: timelineRes } = await api.post(
        `/learning-tutorials/projects/${pid}/generate-timeline`,
        null,
        { timeout: LEARNING_TUTORIAL_TIMELINE_TIMEOUT_MS },
      );
      setFramesPreview(timelineRes.frames_preview || []);
      setStatus('ready');
      toast.success('Lesson ready — narration aligned, open the editor to review');
      navigate(`/learning/${pid}/editor`);
    } catch (err) {
      setStatus('error');
      toast.error(err.response?.data?.detail || 'Pipeline failed');
    }
  };

  const openEditor = () => {
    if (projectId) navigate(`/learning/${projectId}/editor`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <TwinneeBrand />
        <span className="text-sm text-slate-400 flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-emerald-400" />
          Learning & Training Flow
        </span>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-2">Create a tutorial course video</h1>
          <p className="text-slate-400 mb-8">
            Choose slide theme, language, and voice. Layout adapts per scene from your content. AI can add diagrams where
            they help explain the topic. Then review in the editor, confirm, and export.
          </p>

          <div className="space-y-4 rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
            <div>
              <Label>Lesson title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-slate-800" />
            </div>
            <div>
              <Label>Topic</Label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} className="mt-1 bg-slate-800" />
            </div>
            <div>
              <Label>Learning objective</Label>
              <Textarea value={objective} onChange={(e) => setObjective(e.target.value)} className="mt-1 bg-slate-800" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Audience</Label>
                <Input value={audience} onChange={(e) => setAudience(e.target.value)} className="mt-1 bg-slate-800" />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input type="number" min={1} max={60} value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 bg-slate-800" />
              </div>
            </div>
            <div>
              <Label>Script & narration language</Label>
              <Select value={language} onValueChange={setLanguage} disabled={optionsLoading}>
                <SelectTrigger className="mt-1 bg-slate-800">
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  {(options.languages || []).map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prerequisites</Label>
              <Input value={prerequisites} onChange={(e) => setPrerequisites(e.target.value)} className="mt-1 bg-slate-800" />
            </div>
            <div>
              <Label>Key points (one per line)</Label>
              <Textarea value={keyPoints} onChange={(e) => setKeyPoints(e.target.value)} className="mt-1 bg-slate-800" rows={3} />
              <p className="text-xs text-slate-500 mt-1">
                Use hierarchy with slashes, e.g. <span className="text-slate-400">Data types / Primitives / int</span>
                {' '}so the lesson builds nested bullets, explanations, and branch flowcharts per chapter.
              </p>
            </div>
            <div>
              <Label>Visual layout notes</Label>
              <Textarea value={visualNotes} onChange={(e) => setVisualNotes(e.target.value)} className="mt-1 bg-slate-800" rows={2} />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={includeCode}
                onChange={(e) => setIncludeCode(e.target.checked)}
              />
              <span>
                Include code artifacts (Code Example, walkthroughs)
                {isProgrammingTopic(topic, objective, title) ? (
                  <span className="block text-xs text-amber-400/90 mt-0.5">
                    Suggested for programming topics — Pass 2 chooses which artifacts to include (not guaranteed).
                  </span>
                ) : null}
              </span>
            </label>
          </div>

          <div className="mt-6 space-y-4 rounded-2xl border border-slate-700 bg-slate-900/40 p-6">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Palette className="w-5 h-5 text-emerald-400" />
              Slides & visuals
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Color theme</Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-3 items-start">
                  <div className="flex-1 w-full min-w-0">
                    <ThemeSlidePreview theme={selectedTheme} logo={logoPreviewDraft} size="large" />
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-slate-600"
                      onClick={() => setThemeDialogOpen(true)}
                      disabled={optionsLoading}
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Browse all themes
                    </Button>
                    <p className="text-xs text-slate-500 max-w-[200px]">
                      {selectedTheme?.label || 'Theme'}
                      {selectedTheme?.description ? ` — ${selectedTheme.description}` : ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Label>Slide layout</Label>
              <Select value={layoutMode} onValueChange={setLayoutMode} disabled={optionsLoading}>
                <SelectTrigger className="mt-1 bg-slate-800">
                  <SelectValue placeholder="Layout mode" />
                </SelectTrigger>
                <SelectContent>
                  {(options.layout_modes || [
                    { id: 'per_scene', label: 'Auto preset per scene' },
                    { id: 'dynamic', label: 'Dynamic compose' },
                  ]).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                {layoutMode === 'dynamic'
                  ? 'Each scene is packed from its text, images, flowcharts, and code — elements are sized by footprint and placed without overlap.'
                  : 'Preset layouts (code right, diagram center, split media, etc.) are chosen per scene from bullets, code, and visuals.'}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={generateVisuals}
                onChange={(e) => setGenerateVisuals(e.target.checked)}
              />
              Generate AI diagrams / images where helpful (Gemini or OpenAI)
            </label>
            {generateVisuals && (
              <div>
                <Label>Visual AI provider</Label>
                <Select value={visualProvider} onValueChange={setVisualProvider} disabled={optionsLoading}>
                  <SelectTrigger className="mt-1 bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.visual_providers || [])
                      .filter((v) => v.id !== 'none')
                      .map((v) => (
                        <SelectItem key={v.id} value={v.id} disabled={!v.configured}>
                          {v.label}
                          {!v.configured ? ' (not configured)' : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-4 rounded-2xl border border-slate-700 bg-slate-900/40 p-6">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-sky-400" />
              Narration voice
            </h2>
            <p className="text-xs text-amber-200/90 rounded-lg border border-amber-700/50 bg-amber-950/30 px-3 py-2">
              Preview and export require WhisperX word alignment. Install whisperx, torch, and torchaudio on the backend;
              narration sync fails with an error if alignment is unavailable.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>TTS provider</Label>
                <Select
                  value={ttsProvider}
                  onValueChange={(v) => {
                    setTtsProvider(v);
                    setTtsVoiceId('');
                    if (v === 'murf') setTtsVoiceStyle('Promo');
                    else if (v === 'edge') setTtsVoiceStyle('storyteller');
                  }}
                  disabled={optionsLoading}
                >
                  <SelectTrigger className="mt-1 bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.tts_providers || []).map((p) => (
                      <SelectItem key={p.id} value={p.id} disabled={!p.configured}>
                        {p.label}
                        {!p.configured ? ' (not configured)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {ttsProvider === 'edge' && (
                <div>
                  <Label>Edge voice style</Label>
                  <Select value={ttsVoiceStyle} onValueChange={setTtsVoiceStyle}>
                    <SelectTrigger className="mt-1 bg-slate-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(options.edge_voice_styles || []).map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {ttsProvider === 'elevenlabs' && (
                <div>
                  <Label>ElevenLabs voice</Label>
                  <Select value={ttsVoiceId || '_none'} onValueChange={(v) => setTtsVoiceId(v === '_none' ? '' : v)}>
                    <SelectTrigger className="mt-1 bg-slate-800">
                      <SelectValue placeholder="Choose voice" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Default voice</SelectItem>
                      {elevenlabsVoices.map((v) => (
                        <SelectItem key={v.voice_id || v.id} value={v.voice_id || v.id}>
                          {v.name || v.label || v.voice_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {ttsProvider === 'murf' && (
                <MurfVoiceFields
                  language={language}
                  voices={murfVoices}
                  apiLocales={murfLocales}
                  voiceId={ttsVoiceId}
                  voiceStyle={ttsVoiceStyle}
                  onVoiceIdChange={setTtsVoiceId}
                  onVoiceStyleChange={setTtsVoiceStyle}
                  onLocaleChange={setTtsLocale}
                />
              )}
            </div>
          </div>

          {(status === 'working' || status === 'syncing') && (
            <div className="mt-6 space-y-2">
              <p className="text-sm text-slate-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {status === 'syncing'
                  ? 'Syncing narration (TTS + WhisperX word alignment)…'
                  : 'Generating script, visuals, and timeline…'}
              </p>
              <Progress value={status === 'syncing' ? 85 : 45} className="h-2" />
            </div>
          )}

          <div className="mt-8 flex gap-3">
            <Button
              className="bg-emerald-600 hover:bg-emerald-500"
              disabled={status === 'working' || status === 'syncing' || optionsLoading}
              onClick={createAndRun}
            >
              {status === 'working' || status === 'syncing' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Building…
                </>
              ) : (
                <>
                  <TwinneeLogo className="w-4 h-4 mr-2" />
                  Generate tutorial
                </>
              )}
            </Button>
            {projectId && status !== 'working' && status !== 'syncing' && (
              <Button variant="outline" onClick={openEditor}>
                <Play className="w-4 h-4 mr-2" />
                Open editor
              </Button>
            )}
          </div>

          {framesPreview.length > 0 && (
            <div className="mt-10">
              <h2 className="text-lg font-semibold mb-3">Frame breakdown</h2>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {framesPreview.slice(0, 40).map((f) => (
                  <div key={f.index} className="rounded-lg border border-slate-700 p-3 text-sm">
                    <div className="font-medium text-emerald-300">
                      #{f.index} {f.type || f.scene_title || 'frame'}
                    </div>
                    {f.content && <p className="text-slate-300 mt-1">{f.content}</p>}
                    {f.visual_description && (
                      <p className="text-slate-500 mt-1 text-xs">Visual: {f.visual_description}</p>
                    )}
                    {f.narration && <p className="text-slate-400 mt-1 italic">Narration: {f.narration}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </main>

      <ThemePickerDialog
        open={themeDialogOpen}
        onOpenChange={setThemeDialogOpen}
        themes={options.themes || []}
        selectedThemeId={themeId}
        onSelectTheme={setThemeId}
        logoMode={logoMode}
        onLogoModeChange={handleLogoModeChange}
        logoText={logoText}
        onLogoTextChange={setLogoText}
        logoImageUrl={logoImageUrl}
        onLogoImageUrlChange={setLogoImageUrl}
        onLogoFileSelect={handleLogoFileSelect}
      />
    </div>
  );
};

export default LearningTutorialFlowPage;
